"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import BuyerLandingHero from "../components/LandingPage/BuyerLandingHero";
import ShoppingStepsSection from "../components/LandingPage/ShoppingStepsSection.tsx";
import SellByTypesSection from "../components/LandingPage/SellByTypesSection.tsx";
import EnsureTrustSection from "../components/LandingPage/EnsureTrustSection";
import SellYourCarSection from "../components/LandingPage/SellYourCarSection";
import TredSection from "../components/LandingPage/TredSection.tsx";
import MyAreaSection from "../components/LandingPage/MyAreaSection";
import useDataLayer from "../analytics/useDataLayer";
import useDataIsland from "../analytics/useDataIsland";
import { sendPage } from "reaxl-analytics";
import PopularVehiclesSection from "../components/LandingPage/PopularVehiclesSection";
import { Box } from "@iris/react-components";
import BuyerLandingHeroRedesign from "@/components/LandingPage/BuyerLandingHeroRedesign.tsx";
// Eager import: this section owns the LCP element (#benefits-title) on mobile.
// Lazy-loading via next/dynamic delayed LCP by ~31s in measurements.
import BenefitsOfBuyingSection from "@/components/LandingPage/BenefitsOfBuyingSection";
import { normalizeZip } from "@/utilities/zipHelpers";
import { queryLocationByZip } from "@/utilities/locationApi";
import type { GeolocateResult } from "@/app/api/psx-buy/location-eligibility/types";

// Below-the-fold sections code-split via next/dynamic (matches SYC SLP pattern).
// Hero stays eager since it owns the LCP candidate.
const BuyerLandingVehiclesSection = dynamic(
  () => import("@/components/LandingPage/BuyerLandingVehiclesSection")
);
const WhyChooseAutotraderSection = dynamic(
  () => import("@/components/LandingPage/WhyChooseAutotraderSection")
);
const FeesAndTaxesSection = dynamic(
  () => import("@/components/LandingPage/FeesAndTaxesSection")
);
const WeMakeTrustAPrioritySection = dynamic(
  () => import("../components/LandingPage/WeMakeTrustAPrioritySection")
);
const SellYourCarModal = dynamic(
  () => import("../components/LandingPage/SellYourCarModal")
);
const BuyerLandingMyAreaSection = dynamic(
  () => import("../components/LandingPage/BuyerLandingMyAreaSection")
);

type LocationData = Pick<GeolocateResult, 'zip' | 'state' | 'stateName' | 'city' | 'county'>;

type LandingPageContainerProps = {
  initialRedesignEnabled?: boolean;
};

export default function LandingPageContainer({ initialRedesignEnabled = false }: LandingPageContainerProps) {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [locationData, setLocationData] = useState<LocationData | undefined>(undefined);
  const [eligibility, setEligibility] = useState(false);
  const [hasInitializedLocation, setHasInitializedLocation] = useState(false);
  const [isLocationSectionsReady, setIsLocationSectionsReady] = useState(false);
  const dataIsland = useDataIsland();
  const dataLayer = useDataLayer();
  // Server decides the variant in app/page.tsx via getServerBlpRedesignFlag().
  // We render strictly from that value to avoid SSR/CSR flash. The client
  // Optimizely SDK still fires impression events through useDataLayer().
  const isPsxBlpRedesignEnabled = initialRedesignEnabled;
  const [currentZip, setCurrentZip] = useState<string>(dataLayer?.user?.zipCode || "");
  const [inputZip, setInputZip] = useState<string>(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return localStorage.getItem("blp_input_zip") || "";
  });
  const dataIslandParams = {
    page: "slp_psx_buy",
    googlePageName: "slp_psx_buy",
    label: 'slp_psx_buy',
    pageId: 'slp_psx_buy',
    cdl: {
      event: {
        action: 'View',
        element: 'Page',
        tagIndex: 'psx-01',
      },
      page: {
        id: 'slp_psx_buy',
        siteSection: 'Own',
        type: 'SLP',
      },
      pixall: {
        program: 'DealerDotCom',
      },
    },
  };

  const onPageEnter = () => {
    sendPage({
      data: {
        ...dataIslandParams,
      },
      dataLayer,
      isInitialLoad,
      dataIsland,
      userDuck: dataLayer?.userDuck,
      zip: dataLayer?.user?.zipCode,
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Intentional: fires once on mount only. onPageEnter captures dataLayer/dataIsland
  // at mount time, which is the correct snapshot for the initial page-view beacon.
  useEffect(() => {
    if (isInitialLoad) {
      onPageEnter();
      setIsInitialLoad(false);
    }
  }, []);

  const applyLocationData = (data: GeolocateResult | { error: boolean; errorMessage: string }, zipValue: string): void => {
    if (!data || 'error' in data) {
      setLocationData(undefined);
      setEligibility(false);
      return;
    }
    setLocationData({
      zip: data.zip,
      state: data.state,
      stateName: data.stateName,
      city: data.city,
      county: data.county,
    });
    setEligibility(Boolean(data.eligibility));
    setCurrentZip(data.zip || zipValue);
  };

  const resolveLocationByZip = async (zipValue: string): Promise<void> => {
    const data = await queryLocationByZip(zipValue);
    applyLocationData(data, zipValue);
  };

  const handleZipChange = async (zip: string): Promise<void> => {
    const normalizedZip = normalizeZip(zip);
    if (normalizedZip.length !== 5) {
      return;
    }
    const data = await queryLocationByZip(normalizedZip);
    if ('error' in data) {
      throw new Error(data.errorMessage);
    }
    if (typeof window !== "undefined") {
      localStorage.setItem("blp_zip", data?.zip || normalizedZip);
    }
    applyLocationData(data, normalizedZip);
  };

  useEffect(() => {
    if (hasInitializedLocation) {
      return;
    }

    const savedZip = normalizeZip(localStorage.getItem("blp_zip") ?? "");
    const initialZip = savedZip || normalizeZip(dataLayer?.user?.zipCode ?? "");

    if (!initialZip) {
      setIsLocationSectionsReady(true);
      return;
    }

    if (initialZip.length === 5) {
      setHasInitializedLocation(true);
      resolveLocationByZip(initialZip).finally(() =>
        setIsLocationSectionsReady(true),
      );
    }
  }, [dataLayer]);

  const handleInputZipChange = (zip: string): void => {
    const normalizedZip = normalizeZip(zip);
    setInputZip(normalizedZip);
    localStorage.setItem('blp_input_zip', normalizedZip);
  };

  const renderLoader = (label: string) => (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      data-testid="blp-section-loader"
      className="blp-section-loader"
    >
      <style>{`
        .blp-section-pending{display:none}
        .blp-section-loader{
          display:flex;align-items:center;justify-content:center;
          width:100%;min-height:50vh;padding:1rem;
          background:var(--base-color-neutral-50,#f5f5f5);
        }
        .blp-section-loader::after{
          content:"";display:block;width:2.5rem;height:2.5rem;
          border:0.25rem solid var(--base-color-neutral-200,#e0e0e0);
          border-top-color:var(--sem-color-background-brand-primary-strong,#1f6fb2);
          border-radius:50%;
          animation:blp-loader-spin 0.9s linear infinite;
        }
        @keyframes blp-loader-spin{to{transform:rotate(360deg)}}
      `}</style>
    </div>
  );

  return (
    <>
      {isPsxBlpRedesignEnabled ? (
        // New BLP gets the iris:bg-white wrapper; legacy tree renders without it.
        <Box className="iris:bg-white">
          <noscript>
            <style>{`.blp-section-pending{display:block!important}.blp-section-loader{display:none!important}`}</style>
          </noscript>
          <BuyerLandingHeroRedesign
            currentZip={inputZip}
            onZipChange={handleInputZipChange}
          />
          <div className={isLocationSectionsReady ? undefined : 'blp-section-pending'}>
            <BenefitsOfBuyingSection
              isDealerEligible={eligibility}
              locationData={locationData}
              onZipChange={handleZipChange}
            />
          </div>
          {!isLocationSectionsReady && renderLoader("Loading benefits")}
          <BuyerLandingVehiclesSection
            filter={{
              body_style: 'cab,pickup',
            }}
            heading="Trucks for Sale by Owner"
            backgroundColor="var(--sem-color-background-brand-primary, #E3F2FF)"
            sectionClassName="trucks-for-sale-section"
          />
          <div className={isLocationSectionsReady ? undefined : 'blp-section-pending'}>
            <WhyChooseAutotraderSection
              eligibility={eligibility}
              locationData={locationData}
              onZipChange={handleZipChange}
            />
          </div>
          {!isLocationSectionsReady && renderLoader("Loading why choose Autotrader")}
          <div className={isLocationSectionsReady ? undefined : 'blp-section-pending'}>
            <FeesAndTaxesSection
              locationData={locationData}
              eligibility={eligibility}
              currentZip={currentZip}
              onZipChange={handleZipChange}
            />
          </div>
          {!isLocationSectionsReady && renderLoader("Loading fees and taxes")}
          <BuyerLandingVehiclesSection
            filter={{
              price_max: 5000,
            }}
            heading="Cars for Sale Under $5,000 by Owner"
            sectionClassName="cars-for-sale-section"
            backgroundColor="var(--base-color-neutral-0)"
          />
          <WeMakeTrustAPrioritySection />
          <SellYourCarModal />
          <BuyerLandingMyAreaSection collapseAll />
        </Box>
      ) : (
        <>
          <BuyerLandingHero />
          <ShoppingStepsSection />
          <SellByTypesSection />
          <EnsureTrustSection />
          <PopularVehiclesSection />
          <SellYourCarSection />
          <TredSection />
          <MyAreaSection />
        </>
      )}
    </>
  );
}
